import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Box, Center, Group, MantineColor, Paper, Stack, Text, useMantineTheme } from '@mantine/core';
import KiloButton from 'components/KiloButton';
import useInvoices from 'hooks/invoice/useInvoices';
import { Invoice } from 'types';

import InvoiceCard from './InvoiceCard';
import { saveVisibleCount } from '../invoiceViewState';

export const DEFAULT_VISIBLE_ROWS = 3;

export interface InvoiceBucketDefinition {
  key: string;
  label: string;
  statuses: Invoice['status'][];
  ordering: string;
  color: MantineColor;
}

export interface InvoiceBucketState {
  count: number;
  isLoading: boolean;
  isError: boolean;
}

interface InvoiceBucketProps {
  bucket: InvoiceBucketDefinition;
  athleteId?: string;
  enabled: boolean;
  initialVisibleRows: number;
  onStateChange: (key: string, state: InvoiceBucketState) => void;
}

const InvoiceBucket = ({
  bucket,
  athleteId,
  enabled,
  initialVisibleRows,
  onStateChange,
}: InvoiceBucketProps): JSX.Element | null => {
  const theme = useMantineTheme();
  const [visibleRows, setVisibleRows] = useState(initialVisibleRows);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInvoices({
    status: bucket.statuses,
    athlete: athleteId,
    ordering: bucket.ordering,
    enabled,
  });

  const count = data?.pages[0]?.data.count ?? 0;

  const invoices = useMemo(() => data?.pages.map(page => page.data.results).flat() ?? [], [data?.pages]);

  useEffect(() => {
    onStateChange(bucket.key, { count, isLoading, isError });
  }, [bucket.key, count, isLoading, isError, onStateChange]);

  useEffect(() => {
    saveVisibleCount(bucket.key, visibleRows);
  }, [bucket.key, visibleRows]);

  const handleShowMore = async (): Promise<void> => {
    if (visibleRows < invoices.length) {
      setVisibleRows(invoices.length);
      return;
    }
    if (!hasNextPage) {
      return;
    }
    const result = await fetchNextPage();
    const loaded = result.data?.pages.reduce((total, page) => total + page.data.results.length, 0);
    setVisibleRows(loaded ?? invoices.length);
  };

  if (isLoading || count === 0 || (isError && invoices.length === 0)) {
    return null;
  }

  const canShowMore = visibleRows < invoices.length || !!hasNextPage;

  return (
    <Box data-cy={`invoiceBucket-${bucket.key}`}>
      <Group spacing='xs' position='apart' px='xs' pb='xs' noWrap>
        <Group spacing='xs' noWrap>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: theme.fn.themeColor(bucket.color, 6),
            }}
          />
          <Text weight={600}>{bucket.label}</Text>
        </Group>
        <Badge color='gray' variant='light' radius='sm' data-cy='invoiceBucketCount'>
          {count}
        </Badge>
      </Group>

      <Paper radius='lg' p='sm' withBorder>
        <Stack spacing='sm'>
          {invoices.slice(0, visibleRows).map(invoice => (
            <InvoiceCard key={invoice.invoiceId} invoice={invoice} />
          ))}
        </Stack>
        {canShowMore && (
          <Center pt='sm'>
            <KiloButton
              variant='subtle'
              loading={isFetchingNextPage}
              disabled={isFetchingNextPage}
              onClick={handleShowMore}
            >
              Show more
            </KiloButton>
          </Center>
        )}
      </Paper>
    </Box>
  );
};

export default InvoiceBucket;
